import {
    Avatar, Box,
    Button,
    FormControl,
    FormLabel,
    Stack, styled,
    TextField, Tooltip, TooltipProps, tooltipClasses, Switch, useMediaQuery
} from '@mui/material';
import React, {ChangeEvent, RefObject, useCallback, useContext, useState} from 'react';
import {StateContext} from '../ContextProvider';
import html2canvas from 'html2canvas';
import {useTranslation} from 'react-i18next';
import {Circle} from '@uiw/react-color';
import {ColorResult} from '@uiw/color-convert';
import {supabase} from '../../utils/supabase';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import slugify from 'slugify';
import {parseGpxParser} from '../../utils/gpxParser';
import {gpx} from '@tmcw/togeojson';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
        fontSize: '16px',
        fontWeight: 'semi-bold',
    },
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const FormData = ({refObject} : {refObject: RefObject<HTMLDivElement>}) => {
    const { t } = useTranslation(['commons']);
    const {setGpxFile, gpxFile, setXmlParsed, raceData, setRaceData, xmlParsed, setMarkers} = useContext(StateContext);
    const [isLoading, setIsLoading] = useState(false);
    const query = new URLSearchParams(window.location.search);
    let traceId = query.get('id');

    const handleChangeData = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setRaceData((prevState: any) => ({
            ...prevState, // Copie les champs existants
            [event.target.id]: event.target.value, // Remplace uniquement `name`
        }));
    }, [setRaceData]);

    const handleChangeBoolean = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setRaceData((prevState: any) => ({
            ...prevState, // Copie les champs existants
            [event.target.id]: event.target.checked, // Remplace uniquement `name`
        }));
    }, [setRaceData]);

    const handleChangeHour = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(event.target.value);
        const valueRaceTime = value < 0 ? 0 : value;

        setRaceData((prevState: any) => ({
            ...prevState,
            ['hour']: valueRaceTime,
        }));
    }, [setRaceData]);

    const handleChangeMinute = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(event.target.value);
        let hour = value < 0 ? raceData.hour - 1 : value > 59 ? raceData.hour + 1 : raceData.hour;

        setRaceData((prevState: any) => ({
            ...prevState,
            ['hour']: hour,
            ['minute']: value < 0 ? 59 : value > 59 ? 0 : value,
        }));
    }, [setRaceData, raceData]);

    const handleChangeSecond = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        let value = parseInt(event.target.value);
        let minute = value < 0 ? raceData.minute - 1 : value > 59 ? raceData.minute + 1 : raceData.minute;

        setRaceData((prevState: any) => ({
            ...prevState,
            ['minute']: minute,
            ['second']: value < 0 ? 59 : value > 59 ? 0 : value,
        }));
    }, [setRaceData, raceData]);

    const handleChangeColor = useCallback((color: ColorResult) => {
        setRaceData((prevState: any) => ({
            ...prevState,
            ['color']: color.hex
        }));
    }, [setRaceData]);

    const handleChangeTextColor = useCallback((color: ColorResult) => {
        setRaceData((prevState: any) => ({
            ...prevState,
            ['textColor']: color.hex
        }));
    }, [setRaceData]);

    const handleChangeCartography = useCallback((cartography: string) => {
        setRaceData((prevState: any) => ({
            ...prevState,
            ['cartography']: cartography,
            ['textColor']: cartography === 'full_black' ? 'white' : 'black'
        }));
    }, [setRaceData]);

    const downloadImage = async (event: any) => {
        if (!refObject.current || !gpxFile) return;
        setIsLoading(true);

        let gpxPath = gpxFile instanceof File ?
            await uploadFile('gpx', gpxFile, `trace_${slugify(raceData.raceName, '_')}_${Date.now()}.gpx`) : gpxFile;
        const imagePath = await uploadPng();

        if (!imagePath) {
            setIsLoading(false);
            return;
        }

        const payload = {
            color: raceData.color,
            text_color: raceData.textColor,
            carto: raceData.cartography,
            user_name: raceData.firstname,
            gpx: gpxFile instanceof File ? 'https://jkpxijyqalryfacczlvd.supabase.co/storage/v1/object/public/'+gpxPath : gpxPath,
            hike: raceData.hike,
            distance: raceData.raceDistance,
            hour: raceData.hour,
            minute: raceData.minute,
            second: raceData.second,
            day: raceData.day,
            mounth: raceData.mounth,
            year: raceData.year,
            date: `${String(raceData.day).padStart(2, '0')}.${String(raceData.mounth).padStart(2, '0')}.${raceData.year}`,
            name: raceData.raceName,
            more: raceData.more,
            bip: raceData.bip,
            with_topo: raceData.withTopo,
            poster_min: 'https://jkpxijyqalryfacczlvd.supabase.co/storage/v1/object/public/'+imagePath
        };

        const method = traceId ? supabase.from('order').update(payload).eq('order_id', traceId).select() : supabase.from('order').insert(payload).select();
        const { data, error } = await method;

        setIsLoading(false);

        if (!data && !traceId) {
            return;
        }

        traceId = !traceId && data ? data[0].order_id : traceId;

        if (!window.top) {
            window.location.replace('https://ultrace.fr/produit/affiche/?order_id='+ traceId);
        } else {
            window.top.location.replace('https://ultrace.fr/produit/affiche/?order_id='+ traceId);
        }
    };

    const uploadPng = async () => {
        if (!refObject.current) return null;
        const canvas = await html2canvas(refObject.current, { useCORS: true, scale: 3, backgroundColor: 'transparent' });
        return new Promise<string | null>((resolve) => {
            canvas.toBlob(async (blob) => {
                if (!blob) return resolve(null);
                const path = await uploadFile('poster', blob, `poster_${slugify(raceData.raceName, '_')}_${Date.now()}.jpeg`);
                resolve(path);
            }, 'image/jpeg');
        });
    };

    const uploadFile = async (bucket: string, file: File | Blob, fileName: string) => {
        const { data, error } = await supabase.storage.from(bucket).upload(fileName.toLowerCase(), file);
        return error ? null : data?.fullPath;
    };

    const getGpxName = () => {
        if (gpxFile instanceof File) {
            return gpxFile.name;
        }

        if (gpxFile) {
            return gpxFile?.split('/')?.pop()?.split('?')[0];
        }

        return gpxFile;
    };

    const handlUploadGpx = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }
        setGpxFile(event.target.files[0]);
        const xmlGpx = await parseGpxParser(event.target.files[0]);
        setXmlParsed(xmlGpx ?? null);
        setMarkers([]);
    }, [setXmlParsed]);

    const addMarker = () => {
        if (xmlParsed) {
            const test = gpx(xmlParsed);
            const coordinates = test.features.flatMap((f: any) => f.geometry.coordinates);
            const start = coordinates[0];
            const end = coordinates[coordinates.length - 1];

            if (start[0].toPrecision(2) === end[0].toPrecision(2) && start[1].toPrecision(2) === end[1].toPrecision(2)) {
                setMarkers((prev: any) => [
                    ...prev,
                    {
                        id: 'start',
                        lng: coordinates[coordinates.length-1][0],
                        lat: coordinates[coordinates.length-1][1],
                        text: '🏁'
                    }
                ]);
            } else {
                setMarkers((prev: any) => [
                    ...prev,
                    {
                        id: 'start',
                        lng: coordinates[0][0],
                        lat: coordinates[0][1],
                        text: '📍'
                    },
                    {
                        id: 'end',
                        lng: coordinates[coordinates.length-1][0],
                        lat: coordinates[coordinates.length-1][1],
                        text: '🏁'
                    }
                ]);
            }
        }
    };

    return (
      <FormControl style={{paddingLeft: 10, paddingRight: 10}}>
          <Stack marginY={1}>
              <Button
                  component="label"
                  role={undefined}
                  variant="outlined"
                  tabIndex={-1}
                  startIcon={gpxFile ? <CloudUploadIcon /> : <CloudUploadIcon />}
                  style={{borderRadius: 100}}
              >
                  {gpxFile ? getGpxName() : t('form.import-file')}
                  <VisuallyHiddenInput
                      type="file"
                      accept=".gpx"
                      onChange={handlUploadGpx}
                      multiple
                  />
              </Button>
          </Stack>
          <Stack alignContent={'center'} justifyContent={'center'} marginY={1}>
              <FormLabel id="demo-row-radio-buttons-group-label">{t('form.cartography')}</FormLabel>
              <Box flex={'auto'} alignItems={'center'} flexDirection={'row'} display={'flex'}>
                  <BootstrapTooltip title={t('form.dark')} placement="top">
                      <Avatar
                          src="https://thepostrace.com/wp-content/uploads/2023/06/Noir-Encre-300x300.jpg"
                          sx={{ width: 50, height: 50, marginRight: 1, border: raceData.cartography === 'dark' ? '2px solid black' : '2px solid #eee' }}
                          onClick={() => handleChangeCartography('dark')}
                      />
                  </BootstrapTooltip>
                  <BootstrapTooltip title={t('form.light')} placement="top">
                      <Avatar
                          src="https://thepostrace.com/wp-content/uploads/2024/03/topographic-300x300.jpg"
                          sx={{ width: 50, height: 50, marginRight: 1, border: raceData.cartography === 'light' ? '2px solid black' : '2px solid #eee' }}
                          onClick={() => handleChangeCartography('light')}
                      />
                  </BootstrapTooltip>
                  <BootstrapTooltip title={'Outdoor'} placement="top">
                      <Avatar
                          src="https://thepostrace.com/wp-content/uploads/2022/11/mercantour-bg-300x300.jpg.webp"
                          sx={{ width: 50, height: 50, marginRight: 1, border: raceData.cartography === 'outdoor' ? '2px solid black' : '2px solid #eee' }}
                          onClick={() => handleChangeCartography('outdoor')}
                      />
                  </BootstrapTooltip>
                  <BootstrapTooltip title={'Cali'} placement="top">
                      <Avatar
                          src="https://thepostrace.com/wp-content/uploads/2024/03/cali-300x300.jpg.webp"
                          sx={{ width: 50, height: 50, marginRight: 1, border: raceData.cartography === 'cali' ? '2px solid black' : '' }}
                          onClick={() => handleChangeCartography('cali')}
                      />
                  </BootstrapTooltip>
                  <BootstrapTooltip title={'Full Black'} placement="top">
                      <Avatar
                          sx={{
                              width: 50,
                              height: 50,
                              marginRight: 1,
                              border: raceData.cartography === 'full_black' ? '2px solid black' : '2px solid #eee',
                              bgcolor: 'black',
                              color: 'black'
                      }}
                          onClick={() => handleChangeCartography('full_black')}
                      >B</Avatar>
                  </BootstrapTooltip>
                  <BootstrapTooltip title={'Full White'} placement="top">
                      <Avatar
                          color={'white'}
                          sx={{ width: 50, height: 50, marginRight: 1, border: raceData.cartography === 'full_white' ? '2px solid black' : '2px solid #eee', bgcolor: 'white' }}
                          onClick={() => handleChangeCartography('full_white')}
                      ></Avatar>
                  </BootstrapTooltip>
              </Box>
          </Stack>
          <Stack spacing={2} direction="row" marginY={1} alignItems={'center'}>
              <FormLabel>{t('form.showTopo')}</FormLabel>
              <Switch
                  checked={raceData.withTopo}
                  size={'medium'}
                  onChange={handleChangeBoolean}
                  id={'withTopo'}
              />
          </Stack>
          <Stack alignContent={'center'} justifyContent={'center'} marginY={1}>
              <FormLabel id="demo-row-radio-buttons-group-label">{t('form.color')}</FormLabel>
              <Box flex={'auto'} alignItems={'center'} flexDirection={'row'} display={'flex'}>
                  <Circle
                      colors={['#01a89a', '#07addf', '#273574', '#642484', '#e72385', '#e52e20', '#f0801a', '#f69f19', '#ffe209', '#bea674', '#000000', '#ffffff']}
                      style={{ width: '100%' }}
                      onChange={handleChangeColor}
                      pointProps={{style: {border: '1px solid black'}}}
                  />
              </Box>
          </Stack>
          <Stack alignContent={'center'} justifyContent={'center'} marginY={1}>
              <FormLabel id="demo-row-radio-buttons-group-label">{t('form.colorText')}</FormLabel>
              <Box flex={'auto'} alignItems={'center'} flexDirection={'row'} display={'flex'}>
                  <Circle
                      colors={['#01a89a', '#07addf', '#273574', '#642484', '#e72385', '#e52e20', '#f0801a', '#f69f19', '#ffe209', '#bea674', '#000000', '#ffffff']}
                      style={{ width: '100%' }}
                      onChange={handleChangeTextColor}
                      pointProps={{style: {border: '1px solid black'}}}
                  />
              </Box>
          </Stack>
          <Stack spacing={2} direction="row" marginY={1}>
              <TextField
                  label={t('form.title')}
                  value={raceData.raceName}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'raceName'}
                  fullWidth={true}
                  inputProps={{ maxLength: 28 }}
              />
          </Stack>
          <Stack spacing={2} direction="row" marginY={1}>
              <TextField
                  label={t('form.day')}
                  value={raceData.day}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'day'}
              />
              <TextField
                  label={t('form.mounth')}
                  value={raceData.mounth}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'mounth'}
              />
              <TextField
                  label={t('form.year')}
                  value={raceData.year}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'year'}
              />
          </Stack>
          <Stack spacing={2} direction="row" marginY={1}>
              <TextField
                  label={t('form.hour')}
                  value={raceData.hour}
                  size={'small'}
                  onChange={handleChangeHour}
                  id={'hour'}
                  type={'number'}
              />
              <TextField
                  label={t('form.minute')}
                  value={raceData.minute}
                  size={'small'}
                  onChange={handleChangeMinute}
                  id={'minute'}
                  type={'number'}
              />
              <TextField
                  label={t('form.second')}
                  value={raceData.second}
                  size={'small'}
                  onChange={handleChangeSecond}
                  id={'second'}
                  type={'number'}
              />
          </Stack>
          <Stack spacing={2} direction="row" marginY={1}>
              <TextField
                  label={t('form.distance')}
                  value={raceData.raceDistance}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'raceDistance'}
              />
              <TextField
                  label={t('form.hike')}
                  value={raceData.hike}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'hike'}
              />
              <TextField
                  label={t('form.bip')}
                  value={raceData.bip}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'bip'}
              />
          </Stack>
          <Stack spacing={2} direction="row" marginY={1}>
              <TextField
                  label={t('form.name')}
                  value={raceData.firstname}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'firstname'}
                  fullWidth={true}
                  inputProps={{ maxLength: 14 }}
              />
          </Stack>
          <Stack spacing={2} direction="row" marginY={1}>
              <TextField
                  label={t('form.more')}
                  value={raceData.more}
                  size={'small'}
                  onChange={handleChangeData}
                  id={'more'}
                  fullWidth={true}
                  inputProps={{ maxLength: 28 }}
              />
          </Stack>
          {/*<Stack marginY={1}>
              <Button
                  variant="contained"
                  onClick={addMarker}
                  loading={isLoading}
                  style={{ borderRadius: 100}}
                  disabled={gpxFile === null}
              >Ajouter une ville</Button>
          </Stack>*/}
          <Stack marginY={1}>
              <Button
                  variant="contained"
                  onClick={downloadImage}
                  loading={isLoading}
                  style={{ borderRadius: 100}}
                  disabled={gpxFile === null}
              >{t('form.download')}</Button>
          </Stack>
      </FormControl>
  )
};

export default FormData;
